import { call, put, delay } from 'redux-saga/effects';
import { subscriptionActions } from '../actions/subscriptionActions';
import { subscriptionService } from '../services/subscriptionService';
import { subscriptionConstants } from '../actions/constants/subscriptionConstants';
import { openSubscriptionCheckout } from "../utils/razorpay";
import { commonActions } from '@/actions/commonAction';
import { commonConstants } from '@/actions/constants/commonConstants';
import { setAuthToken } from '@/services/axios';

function* getSubscriptionPlansSaga(action: any): Generator<any, void, any> {
  try {
    const response = yield call(subscriptionService.getActivePlans, action.payload.page, action.payload.limit, action.payload.mode);

    if (response && response.success) {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_PLANS_SUCCESS, payload: response.resultObject });
    } else {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_PLANS_FAILURE, payload: response?.errorMessage || 'Failed to fetch active plans' });
    }
  } catch (error: any) {
    yield put(
      { type: subscriptionConstants.GET_SUBSCRIPTION_PLANS_FAILURE, payload: error.response?.data?.errorMessage || error.message || 'An error occurred while fetching plans' }
    );
  }
}

function* getSubscriptionSummarySaga(action: any): Generator<any, void, any> {
  try {
    const response = yield call(subscriptionService.getCurrentSubscription, action?.payload);
    if (response && response.success) {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_SUMMARY_SUCCESS, payload: response.resultObject });
    } else {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_SUMMARY_FAILURE, payload: response?.errorMessage || 'No current subscription found' });
    }
  } catch (error: any) {
    yield put(
      { type: subscriptionConstants.GET_SUBSCRIPTION_SUMMARY_FAILURE, payload: error.response?.data?.errorMessage || error.message || 'An error occurred while fetching current subscription' }
    );
  }
}

function* getSubscriptionHistorySaga(action: any): Generator<any, void, any> {
  try {
    const response = yield call(subscriptionService.getSubscriptionHistory, action.payload.page, action.payload.limit, action.payload.entityId);
    if (response && response.success) {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_PAYMENTS_SUCCESS, payload: response.resultObject });
    } else {
      yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_PAYMENTS_FAILURE, payload: response?.errorMessage || 'Failed to fetch history' });
    }
  } catch (error: any) {
    yield put(
      { type: subscriptionConstants.GET_SUBSCRIPTION_PAYMENTS_FAILURE, payload: error.response?.data?.errorMessage || error.message || 'An error occurred while fetching history' }
    );
  }
}

function* pollSubscriptionStatusSaga(studioSubscriptionId: string): Generator<any, void, any> {
  const MAX_RETRIES = 10;
  const POLL_INTERVAL = 3000; // 3 seconds

  // for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
  while (true) {
    const response = yield call(subscriptionService.checkSubscriptionStatus, studioSubscriptionId);

    if (response?.success) {
      if (response.resultObject.status !== 'pending') {
        if (response.resultObject.token) {
          console.log('Setting auth token in pollSubscriptionStatusSaga:', response.resultObject.token);
          setAuthToken(response.resultObject);
        }
        yield put({
          type: subscriptionConstants.checkSubscriptionStatusSuccess,
          payload: response.resultObject,
        });
        return;
      }
    }

    yield delay(POLL_INTERVAL);
  }

  // }

  yield put({
    type: subscriptionConstants.checkSubscriptionStatusFailure,
    payload: 'Payment processing timed out.',
  });
}


function* prepareSubscriptionCheckoutSaga(action: any): Generator<any, void, any> {
  try {

    const checkoutResponse = yield call(subscriptionService.prepareSubscriptionCheckout, action.payload);

    if (!checkoutResponse?.success) {
      yield put({ type: subscriptionConstants.prepareSubscriptionCheckoutFailure, payload: checkoutResponse?.errorMessage, });
      yield put(commonActions.errorResponse(checkoutResponse?.errorMessage || 'Failed to fetch users'));
      return;
    }

    yield put({ type: subscriptionConstants.prepareSubscriptionCheckoutSuccess, payload: checkoutResponse.resultObject });

    yield call(openSubscriptionCheckout, checkoutResponse.resultObject);
    yield put({ type: subscriptionConstants.checkSubscriptionStatusRequest });
    yield call(pollSubscriptionStatusSaga, checkoutResponse.resultObject.subscriptionId);

    // const verifyResponse = yield call( subscriptionService.verifySubscriptionPayment,
    //     {
    //         studioSubscriptionId:  checkoutResponse.resultObject.studioSubscriptionId,
    //         ...razorpayResponse,
    //     }
    // );

    // if (!verifyResponse?.success) {
    //     yield put({ type: subscriptionConstants.verifySubscriptionPaymentFailure, payload: verifyResponse?.errorMessage });
    //     return;
    // }

    // yield put({ type: subscriptionConstants.verifySubscriptionPaymentSuccess, payload: verifyResponse.resultObject });

    yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_SUMMARY_REQUEST });

    yield put({
      type: subscriptionConstants.GET_SUBSCRIPTION_PAYMENTS_REQUEST,
      payload: {
        page: 1,
        limit: 10,
      },
    });
  } catch (error: any) {
    console.error('Error in prepareSubscriptionCheckoutSaga:', error);
    yield put({ type: subscriptionConstants.prepareSubscriptionCheckoutFailure, payload: error.response?.data?.errorMessage || error.message, });
    yield put(commonActions.errorResponse(error.response?.data?.errorMessage || error.message || 'Failed to fetch users'));
  }
}

function* cancelSubscriptionSaga(action: any): Generator<any, void, any> {
  try {
    const cancelResponse = yield call(subscriptionService.cancelSubscription, action.payload);
    if (!cancelResponse?.success) {
      yield put({ type: subscriptionConstants.cancelSubscriptionFailure, payload: cancelResponse?.errorMessage });
      yield put(commonActions.errorResponse(cancelResponse?.errorMessage || 'Failed to cancel subscription'));
      return;
    }
    yield put({ type: subscriptionConstants.cancelSubscriptionSuccess, payload: cancelResponse.resultObject });
    yield put({ type: subscriptionConstants.GET_SUBSCRIPTION_SUMMARY_REQUEST });
    yield put({
      type: subscriptionConstants.GET_SUBSCRIPTION_PAYMENTS_REQUEST,
      payload: {
        page: 1,
        limit: 10,
      },
    });
  } catch (error: any) {
    console.error('Error in cancelSubscriptionSaga:', error);
    yield put({ type: subscriptionConstants.cancelSubscriptionFailure, payload: error.response?.data?.errorMessage || error.message });
    yield put(commonActions.errorResponse(error.response?.data?.errorMessage || error.message || 'Failed to cancel subscription'));
  }
}

function* viewSubscription(action: any): any {
  try {
    const response = yield call(subscriptionService.viewSubscription, action.payload);
    if (response.success) {
      yield put({ type: subscriptionConstants.viewSubscriptionSuccess, payload: response.resultObject });
    } else {
      yield put({ type: subscriptionConstants.viewSubscriptionFailure, payload: response.message || response.errorMessage });

      yield put(commonActions.errorResponse(response.message || "Failed to view subscription"));
    }
  } catch (error) {
    yield put({ type: subscriptionConstants.viewSubscriptionFailure, payload: error });
  }
}

function* updateSubscription(action: any): any {
  try {
    const response = yield call(subscriptionService.updateSubscription, action.payload);
    if (response.success) {
      yield put({ type: subscriptionConstants.updateSubscriptionSuccess, payload: response.resultObject });
      yield put({ type: commonConstants.showSuccessMessage, payload: response.message })
    } else {
      yield put({ type: subscriptionConstants.updateSubscriptionFailure, payload: response.message || response.errorMessage });

      yield put(commonActions.errorResponse(response.message || "Failed to view subscription"));
    }
  } catch (error) {
    yield put({ type: subscriptionConstants.updateSubscriptionFailure, payload: error });
  }
}

export const subscriptionSaga = {
  getSubscriptionPlansSaga,
  getSubscriptionSummarySaga,
  getSubscriptionHistorySaga,
  prepareSubscriptionCheckoutSaga,
  cancelSubscriptionSaga,
  viewSubscription, updateSubscription
};
